// Empire overview (P33, step 16d): totals and per-system work composed from the queries the client
// already holds (own systems, own fleets).
import type {
  BuildingQueueItem,
  FullSystem,
  OwnFleet,
  PortTrade,
  Resources,
  ShipCounts,
  ShipProductionItem,
  ShipType,
} from '@stars-and-ships/api-types';

export const sumResources = (blocks: Resources[]): Resources =>
  blocks.reduce(
    (sum, r) => ({
      metal: sum.metal + r.metal,
      gas: sum.gas + r.gas,
      supplies: sum.supplies + r.supplies,
    }),
    { metal: 0, gas: 0, supplies: 0 }
  );

function addShips(counts: ShipCounts[]): ShipCounts {
  const total: ShipCounts = {};
  for (const c of counts) {
    for (const [type, n] of Object.entries(c) as [ShipType, number][]) {
      total[type] = (total[type] ?? 0) + n;
    }
  }
  return total;
}

/** Own ships stationed at a system, garrison included (a fleet waiting there does not count). */
export const shipsAt = (fleets: OwnFleet[], systemId: string): ShipCounts =>
  addShips(
    fleets
      .filter((f) => f.status === 'stationed' && f.locationSystemId === systemId)
      .map((f) => f.ships)
  );

/** Every own ship by class, wherever it is. */
export const ownShipTotals = (fleets: OwnFleet[]): ShipCounts =>
  addShips(fleets.map((f) => f.ships));

/** Own fleets away from a station: in transit, returning, waiting, in battle or on a mission. */
export const fleetsInFlight = (fleets: OwnFleet[]): number =>
  fleets.filter((f) => f.status !== 'stationed').length;

export interface RunningWork {
  build: { item: BuildingQueueItem; waiting: number } | null;
  trade: PortTrade | null;
  production: { item: ShipProductionItem; waiting: number } | null;
}

/** The first entry of each queue (the one at work) and how many wait behind it. */
export function runningWork(
  system: Pick<FullSystem, 'buildingQueue' | 'portTrade' | 'shipProduction'>
): RunningWork {
  const [build] = [...system.buildingQueue].sort((a, b) => a.slot - b.slot);
  const [production] = [...system.shipProduction].sort((a, b) => a.position - b.position);
  return {
    build: build ? { item: build, waiting: system.buildingQueue.length - 1 } : null,
    trade: system.portTrade?.status === 'running' ? system.portTrade : null,
    production: production ? { item: production, waiting: system.shipProduction.length - 1 } : null,
  };
}
